import {
    BankAccountBody,
    ConfirmPaymentTokenBody,
    CustomerBody,
    CustomerResponse,
    PaymentTokenBody,
} from "../../sdk/types"
import { useAPIState, useSDK } from "../../context/client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const useQueryCustomerId = (options?: { background?: boolean }) => {
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)
    const customerWeakToken = useAPIState(s => s.customerWeakToken)

    return useQuery({
        queryKey: ["getCustomerId", { token: customerWeakToken }],
        queryFn: ({ signal }) => client.getCustomerId(signal),
        enabled: !!customerWeakToken && !customer_id,
        meta: { background: options?.background, errorToast: true },
    })
}

const useQueryCustomer = (options?: { background?: boolean }) => {
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)

    return useQuery({
        queryKey: ["getCustomer", { id: customer_id }],
        queryFn: ({ signal }) => client.getCustomer(customer_id as string, undefined, undefined, signal),
        enabled: !!customer_id,
        meta: { background: options?.background, errorToast: true },
    })
}

const useMutationCustomer = (token?: "customerWeakToken") => {
    const queryClient = useQueryClient()
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)

    return useMutation({
        mutationFn: (body: CustomerBody) => client.updateCustomer(customer_id as string, body, token),
        onSuccess: data => {
            queryClient.setQueryData(["getCustomer", { id: customer_id }], data)
        },
    })
}

const usePostBankAccount = () => {
    const queryClient = useQueryClient()
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)

    return useMutation({
        mutationFn: (body: BankAccountBody) => client.createBankAccount(customer_id as string, body),
        onSuccess: data => {
            queryClient.setQueryData<CustomerResponse>(["getCustomer", { id: customer_id }], oldData => {
                if (!oldData) return
                return {
                    ...oldData,
                    payment: {
                        ...oldData.payment,
                        bank_accounts: [...oldData.payment.bank_accounts, data],
                    },
                }
            })
        },
    })
}

const useMutationBankAccount = (id: string) => {
    const queryClient = useQueryClient()
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)

    return useMutation({
        mutationFn: (body: BankAccountBody) => client.updateBankAccount(customer_id as string, id, body),
        onSuccess: data => {
            queryClient.setQueryData<CustomerResponse>(["getCustomer", { id: customer_id }], oldData => {
                if (!oldData) return
                return {
                    ...oldData,
                    payment: {
                        ...oldData.payment,
                        bank_accounts: oldData.payment.bank_accounts.map(ba => (ba.id === data.id ? data : ba)),
                    },
                }
            })
        },
    })
}

const useRemoveBankAccount = () => {
    const queryClient = useQueryClient()
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)

    return useMutation({
        mutationFn: (id: string) => client.removeBankAccount(customer_id as string, id),
        onSuccess: (_, id) => {
            queryClient.setQueryData<CustomerResponse>(["getCustomer", { id: customer_id }], oldData => {
                if (!oldData) return
                return {
                    ...oldData,
                    payment: {
                        ...oldData.payment,
                        bank_accounts: oldData.payment.bank_accounts.filter(ba => ba.id !== id),
                    },
                }
            })
        },
    })
}

const useRequestPaymentToken = () => {
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)

    return useMutation({
        mutationFn: (body: PaymentTokenBody) => client.requestPaymentToken(customer_id as string, body),
    })
}

const useConfirmPaymentToken = () => {
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)

    return useMutation({
        mutationFn: (body: ConfirmPaymentTokenBody) => client.confirmPaymentToken(customer_id as string, body),
    })
}

export {
    useQueryCustomerId,
    useQueryCustomer,
    useMutationCustomer,
    usePostBankAccount,
    useMutationBankAccount,
    useRemoveBankAccount,
    useRequestPaymentToken,
    useConfirmPaymentToken,
}
